// src/components/chat/MessageBubble.tsx
// Individual message display component

import React, { useState } from 'react';
import { clsx } from 'clsx';
import { format } from 'date-fns';
import { Bot, User, FileText, FileSpreadsheet, File, ChevronDown, ChevronRight, Sparkles, ImageOff, X, CircleStop } from 'lucide-react';
import BlockReferenceAttachment from './BlockReferenceAttachment';
import SegmentRenderer from './SegmentRenderer';
import type { ChatMessage, ChatMessageAttachment, ArtifactInfo } from '@/types/chat';

interface MessageBubbleProps {
  message: ChatMessage;
  onArtifactClick?: (artifact: ArtifactInfo) => void;
  onToolCallClick?: (toolCallId: string) => void;
}

const ImageAttachment: React.FC<{ attachment: ChatMessageAttachment }> = ({ attachment }) => {
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const displayUrl = attachment.data?.displayUrl as string | undefined;
  const filename = (attachment.data?.filename as string) || 'Image';

  if (!displayUrl || failed) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-accent text-muted-foreground text-xs">
        <ImageOff className="w-4 h-4" />
        <span>{filename}</span>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => setExpanded(true)}
        className="block rounded-lg overflow-hidden max-w-xs hover:opacity-90 transition-opacity"
      >
        <img
          src={displayUrl}
          alt={filename}
          className="max-h-64 object-contain"
          onError={() => setFailed(true)}
        />
      </button>

      {expanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setExpanded(false)}
        >
          <button
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20"
            onClick={() => setExpanded(false)}
          >
            <X className="w-5 h-5" />
          </button>
          <img
            src={displayUrl}
            alt={filename}
            className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

const PdfAttachment: React.FC<{ attachment: ChatMessageAttachment }> = ({ attachment }) => {
  const filename = (attachment.data?.filename as string) || 'Document.pdf';
  const pageCount = attachment.data?.pageCount as number | undefined;

  return (
    <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-destructive/15 border border-destructive/20 max-w-xs">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-destructive/15 flex items-center justify-center">
        <FileText className="w-5 h-5 text-destructive" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{filename}</p>
        <p className="text-xs text-muted-foreground">
          PDF{pageCount ? ` · ${pageCount} page${pageCount === 1 ? '' : 's'}` : ''}
        </p>
      </div>
    </div>
  );
};

const DocumentAttachment: React.FC<{ attachment: ChatMessageAttachment }> = ({ attachment }) => {
  const filename = (attachment.data?.filename as string) || 'Document';
  const mimeType = (attachment.data?.mimeType as string) || '';
  const fileSizeBytes = attachment.data?.fileSizeBytes as number | undefined;

  const isSpreadsheet = mimeType === 'text/csv' ||
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const isDocx = mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

  const IconComponent = isSpreadsheet ? FileSpreadsheet : isDocx ? FileText : File;
  const iconColor = isSpreadsheet
    ? 'text-success'
    : isDocx
      ? 'text-info'
      : 'text-muted-foreground';
  const bgColor = isSpreadsheet
    ? 'bg-success/15 border-success/20'
    : isDocx
      ? 'bg-info/15 border-info/20'
      : 'bg-muted border-border';
  const iconBg = isSpreadsheet
    ? 'bg-success/15'
    : isDocx
      ? 'bg-info/15'
      : 'bg-accent';

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border max-w-xs ${bgColor}`}>
      <div className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${iconBg}`}>
        <IconComponent className={`w-5 h-5 ${iconColor}`} />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{filename}</p>
        {fileSizeBytes && (
          <p className="text-xs text-muted-foreground">{formatSize(fileSizeBytes)}</p>
        )}
      </div>
    </div>
  );
};

const ArtifactChip: React.FC<{
  attachment: ChatMessageAttachment;
  onClick?: (artifact: ArtifactInfo) => void;
}> = ({ attachment, onClick }) => {
  const data = attachment.data || {};
  const title = (data.title as string) || 'Generated file';
  const fileType = (data.fileType as string) || 'pdf';
  const version = (data.version as number) || 1;
  const fileSizeBytes = data.fileSizeBytes as number | undefined;

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleClick = () => {
    if (!onClick) return;
    onClick({
      artifactId: data.artifactId as string,
      artifactGroupId: data.artifactGroupId as string,
      version,
      fileType,
      title,
      filename: (data.filename as string) || `${title}.${fileType}`,
      fileSizeBytes: fileSizeBytes || 0,
    });
  };

  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-accent border border-primary/40 max-w-xs hover:bg-accent transition-colors text-left"
    >
      <div className={clsx(
        'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
        (fileType === 'xlsx' || fileType === 'csv') ? 'bg-success/15'
          : fileType === 'docx' ? 'bg-info/15'
          : fileType === 'pptx' ? 'bg-warn/15'
          : 'bg-accent'
      )}>
        {(fileType === 'xlsx' || fileType === 'csv') ? (
          <FileSpreadsheet className="w-5 h-5 text-success" />
        ) : fileType === 'docx' ? (
          <FileText className="w-5 h-5 text-info" />
        ) : fileType === 'pptx' ? (
          <FileText className="w-5 h-5 text-warn" />
        ) : (
          <FileText className="w-5 h-5 text-primary" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-foreground truncate">{title}</p>
        <p className="text-xs text-muted-foreground">
          {fileType.toUpperCase()} · v{version}
          {fileSizeBytes ? ` · ${formatSize(fileSizeBytes)}` : ''}
        </p>
      </div>
    </button>
  );
};

const MessageBubble: React.FC<MessageBubbleProps> = ({ message, onArtifactClick, onToolCallClick }) => {
  const isUser = message.role === 'user';
  const timestamp = format(new Date(message.created_at), 'h:mm a');
  const [showThinking, setShowThinking] = useState(false);

  const hasThinking = !isUser && !!message.thinking_content;

  return (
    <div
      className={clsx(
        'flex gap-3 max-w-[85%]',
        isUser ? 'ml-auto flex-row-reverse' : 'mr-auto'
      )}
    >
      {/* Avatar */}
      <div
        className={clsx(
          'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
          isUser
            ? 'bg-primary'
            : 'bg-gradient-to-br from-primary/70 to-primary'
        )}
      >
        {isUser ? (
          <User className="w-4 h-4 text-primary-foreground" />
        ) : (
          <Bot className="w-4 h-4 text-primary-foreground" />
        )}
      </div>

      {/* Message content */}
      <div className={clsx('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
        {/* Persisted thinking (collapsible) */}
        {hasThinking && (
          <div className="w-full rounded-xl border border-primary/40 bg-accent/50 overflow-hidden mb-1">
            <button
              onClick={() => setShowThinking(prev => !prev)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs font-medium text-primary hover:bg-accent/50 transition-colors"
            >
              {showThinking ? (
                <ChevronDown className="w-3.5 h-3.5" />
              ) : (
                <ChevronRight className="w-3.5 h-3.5" />
              )}
              <Sparkles className="w-3.5 h-3.5" />
              <span>Show thinking</span>
            </button>
            {showThinking && (
              <div className="px-3 pb-2.5 max-h-64 overflow-y-auto">
                <p className="text-xs text-primary/80 dark:text-primary/70 whitespace-pre-wrap break-words leading-relaxed font-mono">
                  {message.thinking_content}
                </p>
              </div>
            )}
          </div>
        )}

        {/* User file attachments (images, PDFs, and documents) */}
        {isUser && message.attachments && message.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {message.attachments
              .filter(att => att.type === 'user_image')
              .map((att, index) => (
                <ImageAttachment key={`img-${index}`} attachment={att} />
              ))}
            {message.attachments
              .filter(att => att.type === 'user_pdf')
              .map((att, index) => (
                <PdfAttachment key={`pdf-${index}`} attachment={att} />
              ))}
            {message.attachments
              .filter(att => att.type === 'user_document')
              .map((att, index) => (
                <DocumentAttachment key={`doc-${index}`} attachment={att} />
              ))}
          </div>
        )}

        {/* Bubble */}
        <div
          className={clsx(
            'px-4 py-2.5 rounded-2xl',
            isUser
              ? 'bg-primary text-primary-foreground rounded-br-md'
              : 'bg-muted text-foreground rounded-bl-md'
          )}
        >
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap break-words">
              {message.segments
                .filter((s): s is typeof s & { type: 'text' } => s.type === 'text')
                .map(s => s.text)
                .join('\n\n')}
            </p>
          ) : (
            <SegmentRenderer segments={message.segments} chatThreadId={message.thread_id} onToolCallClick={onToolCallClick} />
          )}
        </div>

        {/* Block reference attachments (inline visualizations) */}
        {!isUser && message.attachments && message.attachments.length > 0 && (
          <div className="flex flex-col gap-2 mt-2 w-full max-w-[600px]">
            {message.attachments.map((att, index) => {
              if (att.type === 'block_reference' && att.data?.block_id) {
                return (
                  <BlockReferenceAttachment
                    key={`att-${index}`}
                    blockId={att.data.block_id as string}
                  />
                );
              }
              return null;
            })}
          </div>
        )}

        {/* Generated artifact attachments */}
        {!isUser && message.attachments && message.attachments.some(att => att.type === 'generated_artifact') && (
          <div className="flex flex-wrap gap-2 mt-2">
            {message.attachments
              .filter(att => att.type === 'generated_artifact')
              .map((att, index) => (
                <ArtifactChip
                  key={`artifact-${index}`}
                  attachment={att}
                  onClick={onArtifactClick}
                />
              ))}
          </div>
        )}

        {/* Stopped indicator (decision 7 — survives reload via chat_messages.stop_reason) */}
        {!isUser && message.stop_reason === 'aborted' && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs text-muted-foreground bg-muted border border-border">
            <CircleStop className="w-3 h-3" />
            Stopped
          </span>
        )}

        {/* Timestamp */}
        <span
          className={clsx(
            'text-xs text-muted-foreground',
            isUser ? 'text-right' : 'text-left'
          )}
        >
          {timestamp}
        </span>
      </div>
    </div>
  );
};

export default MessageBubble;
